/**
 * Geteilte Kontext-Identität für die Kunden-/Cross-Login-Anzeige.
 *
 * Kapselt die mode-Ableitung (eigene Sicht vs. alle/ein/mehrere Debitoren),
 * die daraus resultierende Avatar-Darstellung, den Primärnamen und eine
 * Subline. Wird sowohl vom Mobile-Header (Avatar-Pill) als auch von der
 * Desktop-Trigger-Zeile (`CustomerContextPopover`) genutzt, damit beide
 * dieselbe Logik teilen statt sie zu duplizieren.
 */

export type ContextMode = 'off' | 'all' | 'single' | 'multi';

/** Wie der Avatar gefüllt wird — Initialen, Anzahl (Multi) oder generisches Icon (Alle). */
export type AvatarSpec =
  | { type: 'initials'; value: string }
  | { type: 'count'; value: number }
  | { type: 'icon' };

export interface ContextIdentityInput {
  user:
    | {
        name?: string | null;
        email?: string | null;
        customerNumber?: string | null;
      }
    | null
    | undefined;
  isAdmin: boolean;
  allCustomers: boolean;
  selectedCustomers: { customerNumber: string; customerName: string }[];
}

export interface ContextIdentity {
  mode: ContextMode;
  /** Primärer Anzeigename (angemeldeter Nutzer bzw. aktiver Debitor-Kontext). */
  primaryName: string;
  /** Kurze Statuszeile unter dem Namen (nur Desktop-Trigger). */
  subline: string;
  /** Tailwind-Klassen für den Avatar-Hintergrund. */
  avatarBg: string;
  /** Darstellung des Avatars. */
  avatar: AvatarSpec;
}

/** Initialen aus einem Namen (max. 2 Zeichen); Fallback „–". */
export function getInitials(name: string | undefined | null): string {
  if (!name) return '–';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

const GRADIENT_BG = 'bg-gradient-to-br from-primary to-primary-hover';
const NEUTRAL_BG = 'bg-zinc-700';

export function getContextIdentity({
  user,
  isAdmin,
  allCustomers,
  selectedCustomers,
}: ContextIdentityInput): ContextIdentity {
  const mode: ContextMode = !allCustomers
    ? 'off'
    : selectedCustomers.length === 0
      ? 'all'
      : selectedCustomers.length === 1
        ? 'single'
        : 'multi';

  switch (mode) {
    case 'single':
      return {
        mode,
        primaryName: selectedCustomers[0].customerName,
        subline: 'Cross-Login aktiv',
        avatarBg: GRADIENT_BG,
        avatar: { type: 'initials', value: getInitials(selectedCustomers[0].customerName) },
      };
    case 'multi':
      return {
        mode,
        primaryName: 'Multi Kunden',
        subline: `${selectedCustomers.length} Debitoren gewählt`,
        avatarBg: NEUTRAL_BG,
        avatar: { type: 'count', value: selectedCustomers.length },
      };
    case 'all':
      return {
        mode,
        primaryName: 'Alle Debitoren',
        subline: 'Cross-Login · alle sichtbar',
        avatarBg: NEUTRAL_BG,
        avatar: { type: 'icon' },
      };
    default:
      return {
        mode,
        primaryName: user?.name ?? 'Konto',
        subline: isAdmin
          ? 'Nur meine Daten'
          : user?.customerNumber
            ? `KD-Nr. ${user.customerNumber}`
            : (user?.email ?? ''),
        avatarBg: GRADIENT_BG,
        avatar: { type: 'initials', value: getInitials(user?.name) },
      };
  }
}
